import { ButtonVanilla, CloseIcon, Typography, useMediaQuery } from '@ag1/ui';
import { mapCountryCodeToStore } from '@portal/appLayer/nextjsMiddleware/utils/detectRequestGeoStore';
import { DAYS_IN_WEEK } from '@portal/constants/time-constants';
import { StoreEnum, StoreOptions } from '@portal/schema/storeCode';
import { useLocale } from '@portal/shared/hooks/useLocale';
import { SSGates } from '@portal/state/context/StatsigContext/StatsigFlags';
import { useCloudfrontLocationQuery } from '@portal/state/hooks/useCloudfrontLocation';
import { useStoreValue } from '@portal/state/hooks/useStore';
import { useQueryGetCommonContentData } from '@portal/state/studio/commonContentData/getCommonContentData';
import { ALL_COUNTRIES } from '@portal/state/utils/constantAllCountries';
import { useGateValue } from '@statsig/react-bindings';
import cookies from 'js-cookie';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as styles from './LocaleDetectionBanner.css';

const SUGGESTION_DISMISSED_COOKIE = 'ag-locale-suggestion-dismissed';

export const LocaleDetectionBanner = () => {
  const [isOpen, setIsOpen] = useState(false);
  const locale = useLocale();
  const store = useStoreValue();
  const atMedium = useMediaQuery('m');
  const buttonSize = atMedium ? 'M' : 'S';
  const { data: commonContentData } = useQueryGetCommonContentData({ locale });

  const isLocaleDetectionEnabled = useGateValue(SSGates.enableLocaleDetectionBanner);

  const { data } = useCloudfrontLocationQuery();
  const detectedCountryCode = data?.country;
  const detectedCountry =
    data?.countryName || ALL_COUNTRIES.find((c) => c.code === detectedCountryCode)?.name;

  const suggestedStore: StoreEnum | null = useMemo(() => {
    if (!detectedCountryCode) return null;

    const detectedCountryToStore = mapCountryCodeToStore(detectedCountryCode);
    if (detectedCountryToStore === store) return null; // Same store

    return detectedCountryToStore;
  }, [store, detectedCountryCode]);

  const currentStoreDisplayName = useMemo(
    () =>
      StoreOptions.find((option) => option.value === store)?.countryLabel ||
      store.toLocaleUpperCase(),
    [store]
  );

  const suggestionMessage = useMemo(() => {
    const sanityMessage = commonContentData?.localeSuggestionBanner?.localeSuggestionMessage;
    if (!sanityMessage || !detectedCountry) return null;

    return sanityMessage.replace('{location}', detectedCountry || '');
  }, [commonContentData, detectedCountry]);

  const hasDismissedPromotion = cookies.get(SUGGESTION_DISMISSED_COOKIE);

  const setCookie = () => {
    cookies.set(SUGGESTION_DISMISSED_COOKIE, 'true', { expires: DAYS_IN_WEEK });
  };

  const handleClose = () => {
    setIsOpen(false);
    setCookie();
  };

  const exitButton = (
    <ButtonVanilla
      icon={<CloseIcon size="20" />}
      iconPosition="left"
      isIconOnly
      variant="tertiaryOnDark"
      className={styles.exitCta}
      onClick={() => handleClose()}
    />
  );

  useEffect(() => {
    const isValid =
      isLocaleDetectionEnabled &&
      !!suggestionMessage &&
      !!suggestedStore &&
      !!detectedCountry &&
      !!detectedCountryCode &&
      !hasDismissedPromotion;

    setIsOpen(isValid);
  }, [
    isLocaleDetectionEnabled,
    suggestionMessage,
    suggestedStore,
    detectedCountry,
    detectedCountryCode,
    hasDismissedPromotion
  ]);

  const changeToSuggestedStore = useCallback(() => {
    if (!suggestedStore) return;

    setCookie();

    const url = new URL(window.location.href);
    url.searchParams.set('store', suggestedStore);
    window.location.href = url.toString();
  }, [suggestedStore]);

  if (!isOpen) return null;

  return (
    <div className={styles.container} id="ag-locale-detection-banner">
      <div className={styles.textContainer}>
        <Typography variant="body" size="1" noPadding>
          {suggestionMessage}
        </Typography>
        {!atMedium && exitButton}
      </div>
      <div className={styles.ctaContainer}>
        <div className={styles.ctaSubContainer}>
          <ButtonVanilla
            variant="primaryOnDark"
            size={buttonSize}
            className={styles.cta}
            onClick={changeToSuggestedStore}>
            {detectedCountry}
          </ButtonVanilla>
          <ButtonVanilla
            variant="tertiaryOnDark"
            size={buttonSize}
            className={styles.cta}
            onClick={() => handleClose()}>
            {currentStoreDisplayName}
          </ButtonVanilla>
        </div>
        {atMedium && exitButton}
      </div>
    </div>
  );
};
